/* eslint-disable @nx/enforce-module-boundaries */
import {
  Brifecase,
  Currency,
  Industry,
  SaveJobs,
  LocationIcon,
  CompanyIndustryIcon,
  JobLocationIcon,
} from '@mybridge/icons';
import { SavedJobIcon } from '@mybridge/icons/SavedJob';
import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Image,
  Link,
  SeeMore,
  Stack,
  Text,
  VStack,
} from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useNumberFormatter } from '@mybridge/ui/custom-hooks/useNumberFormatter';
import { useRouter } from 'next/router';
import { forwardRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';
import {
  convertHTMLToPlainText,
  currencyFormatter,
  formatLocation,
} from 'v4/lib/commons';
import { EMPLOYMENT_CHOICES, WORKPLACE_TYPE } from 'v4/lib/data';

export const SavedJobCard = forwardRef(
  (
    {
      isApplied = false,
      onApplyClick,
      job,
      onViewDetailsClick,
      onSaveClick,
      saveLoading,
      isSaved,
      href,
      ...props
    },
    ref
  ) => {
    const { job_details: jd } = job ?? {};
    const {
      hiring_company_details: company,
      currency_choice: currency,
      compensation_range_min_amt: minSalary,
      compensation_range_max_amt: maxSalary,
      benefits,
      employment_type: empType,
      job_description: description,
      payment_choice,
      remote,
    } = jd ?? {};
    const { t } = useTranslator();
    const { formatNumber } = useNumberFormatter();
    const {companyPageList} = useSelector((state) => state.companyPage);
    const isPrivatePage = companyPageList?.some((page) => page?.id === job?.job_details?.hiring_company_details?.id);
    const { push } = useRouter();
    const descPlainText = convertHTMLToPlainText(description ?? '');
    const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const prevDefaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo_placeholder_image.png"
    const defaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo-placeholder-image.png"

    return (
      <>
        <Stack {...props} h="100%">
          {/* Header Section */}
          <HStack alignItems="flex-start" mb={2}>
            <HStack flex={1}>
              <Image
                border="1px solid"
                borderColor="blackAlpha.100"
                src={company?.image?.includes("logo_placeholder_image.png") ? defaultCompanyImg : company?.image ?? defaultCompanyImg}
                w="12"
                h="12"
                borderRadius="50%"
                alt={company?.name}
              />
              <VStack align="start" spacing={0} ml={2}>
                {' '}
                <Heading
                  {...(href ? { as: Link, href } : {})}
                  color="#2E2E2A"
                  size="sm"
                  fontWeight="500"
                >
                  {jd?.title}
                </Heading>
                <Text size="14px" color="#5B5B5B" fontWeight="500">
                  {jd?.days_posted === 0 ? "few minutes ago" : `${formatNumber(jd?.days_posted)} ${t('jobs.daysAgo') || "days ago"}`}
                </Text>{' '}
              </VStack>
            </HStack>
          </HStack>
          
          {/* Content Section */}
          <Stack flex={1} spacing={2}>
            <HStack
              alignItems="flex-start"
              cursor="pointer"
              onClick={() => {
                isPrivatePage
                  ? push(`/page/private/${job?.job_details?.hiring_company_details?.id}`)
                  : push(`/c/${job?.job_details?.hiring_company_details?.slug}`);
              }}
            >
              <CompanyIndustryIcon color="#9F9F9F" />
              <Text display="inline" fontSize="sm" isTruncated>
                {company?.name}
              </Text>
            </HStack>
            {formatLocation(jd) && (
              <HStack alignItems="flex-start">
                <JobLocationIcon color="#9F9F9F" />
                <Text display="inline" fontSize="sm" isTruncated>
                  {formatLocation(jd)}
                </Text>
              </HStack>
            )}
            {minSalary > 0 && (
              <HStack alignItems="flex-start">
                <Box pt="2px">
                  <Currency color="#9F9F9F" />
                </Box>
                {maxSalary !== null && maxSalary > 0 ? (
                  <Text fontSize="sm" isTruncated>
                    {currencyFormatter(minSalary, currency)} to{' '}
                    {currencyFormatter(maxSalary, currency)} {payment_choice}
                  </Text>
                ) : (
                  <Text fontSize="sm" isTruncated>
                    {currencyFormatter(minSalary, currency)} {payment_choice}
                  </Text>
                )}
              </HStack>
            )}
            {benefits?.length > 0 && (
              <HStack alignItems="flex-start">
                <Box pt="2px">
                  <Industry color="#9F9F9F" />
                </Box>
                <Text fontSize="sm" isTruncated={!isMobile} >
                  {benefits?.map?.((b) => b?.name)?.join(', ')}
                </Text>
              </HStack>
            )}
            <HStack alignItems="flex-start">
              <Box pt="2px">
                <Brifecase color="#9F9F9F" />
              </Box>
              <Text fontSize="sm" isTruncated>
                {EMPLOYMENT_CHOICES?.find((ec) => ec.value === empType)?.label}{' '}
                | {WORKPLACE_TYPE?.find((ec) => ec.value === remote)?.label}
              </Text>
            </HStack>
          </Stack>

          {/* Footer Section: Action Buttons */}
          <HStack justifyContent="flex-start">
            {isApplied ? (
              <Button
              cursor="default"
                border="1px solid #38A169"
                borderRadius="50px"
                bg="none"
                fontSize="14px"
                color="green.500"
              >
                {t('jobs.applied') || "Applied"} | {formatDate(jd?.applied_date)}
              </Button>
            ) : (
              <Button
                onClick={onApplyClick}
                border="1px solid gray"
                borderRadius="50px"
                bg="none"
                color="#5B5B5B"
                fontSize="14px"
              >
                {t('pageJobs.apply') || "Apply"}
              </Button>
            )}
            <Button
              onClick={onViewDetailsClick}
              border="1px solid gray"
              borderRadius="50px"
              bg="none"
              color="#5B5B5B"
              fontSize="14px"
            >
              {t('pageJobs.viewDetails') || "View Details"}
            </Button>
            <IconButton
              isLoading={saveLoading}
              onClick={onSaveClick}
              border="1px solid gray"
              borderRadius="50px"
              bg="none"
              color="#5B5B5B"
              fontSize="14px"
              icon={
                isSaved ? (
                  <SavedJobIcon width="20" height="20" />
                ) : (
                  <SaveJobs width="20" height="20" />
                )
              }
            />
          </HStack>
        </Stack>
      </>
    );
  }
);
